/**
 * VeyroCam — Accordion
 *
 * The markup is a plain <details>/<summary> group, which is already fully
 * functional, keyboard-operable and screen-reader correct with no JavaScript.
 * This file adds exactly one behaviour on top: optional single-open mode,
 * where opening one panel closes its siblings.
 *
 * Design note — why the browser keeps control of `open`
 * ----------------------------------------------------
 * An earlier version intercepted the click, called preventDefault() and
 * animated the element's height with the Web Animations API, setting `open`
 * from the animation's finish callback. That put the element's state behind
 * an async callback: when the animation was cancelled or interrupted — by a
 * second click, or by single-open mode closing the same element — the
 * callback never ran and the panel stayed stuck open.
 *
 * Here the browser owns `open` and toggles it synchronously. The script only
 * reacts to the resulting `toggle` event, so there is no state to fall out of
 * sync. The panel's entrance is animated in CSS instead, which cannot leave
 * the element in a broken state no matter how fast it is clicked.
 */

(function () {
  'use strict';

  if (customElements.get('veyro-accordion')) return;

  customElements.define(
    'veyro-accordion',
    class VeyroAccordion extends HTMLElement {
      connectedCallback() {
        this.single = this.dataset.single === 'true';
        this.onToggle = this.onToggle.bind(this);

        // `toggle` does not bubble, so it has to be caught in the capture
        // phase rather than relying on delegation.
        this.addEventListener('toggle', this.onToggle, true);
      }

      disconnectedCallback() {
        this.removeEventListener('toggle', this.onToggle, true);
      }

      onToggle(event) {
        if (!this.single) return;

        var details = event.target;
        if (!details || details.tagName !== 'DETAILS') return;

        // Only act when a panel has just been opened; closing needs nothing.
        if (!details.open) return;

        // Guard against a nested accordion's events being handled here.
        if (details.closest('veyro-accordion') !== this) return;

        this.querySelectorAll('details[open]').forEach(function (other) {
          if (other !== details) other.open = false;
        });
      }
    }
  );
})();
